import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Trash2, Tag } from "lucide-react";

type OfferType = "percent" | "fixed" | "perk";
type OfferTier = "all" | "high_value" | "low_engagement";

interface Offer {
  id: string;
  enabled: boolean;
  label: string;
  offer_type: OfferType;
  value: number | null;
  perk_text: string | null;
  max_value: number | null;
  min_spend: number | null;
  expiry_days: number | null;
  tier: OfferTier;
  applies_to: string | null;
}

interface DraftOffer {
  label: string;
  offer_type: OfferType;
  value: string;
  perk_text: string;
  max_value: string;
  min_spend: string;
  expiry_days: string;
  tier: OfferTier;
  applies_to: string;
  enabled: boolean;
}

const TIER_LABELS: Record<OfferTier, string> = {
  all: "All dormant customers",
  high_value: "High-value customers",
  low_engagement: "Low-engagement customers",
};

function emptyDraft(): DraftOffer {
  return {
    label: "",
    offer_type: "percent",
    value: "",
    perk_text: "",
    max_value: "",
    min_spend: "",
    expiry_days: "",
    tier: "all",
    applies_to: "",
    enabled: true,
  };
}

function summarize(o: Offer): string {
  const scope = o.applies_to?.trim();
  let core: string;
  if (o.offer_type === "percent") core = `${o.value}% off${scope ? ` ${scope}` : ""}`;
  else if (o.offer_type === "fixed") core = `$${o.value} off${scope ? ` ${scope}` : ""}`;
  else core = scope ? `${o.perk_text || "perk"} (${scope})` : (o.perk_text || "perk");
  const bits = [core];
  if (o.min_spend != null) bits.push(`min spend $${o.min_spend}`);
  if (o.expiry_days != null) bits.push(`valid ${o.expiry_days} days`);
  return bits.join(" · ");
}

const WinbackOffers = () => {
  const { businessId } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [draft, setDraft] = useState<DraftOffer | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    if (!businessId) return;
    setIsLoading(true);
    const { data, error } = await supabase
      .from("business_offers")
      .select("id, enabled, label, offer_type, value, perk_text, max_value, min_spend, expiry_days, tier, applies_to")
      .eq("business_id", businessId)
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Couldn't load offers", description: error.message, variant: "destructive" });
    } else {
      setOffers((data ?? []) as Offer[]);
    }
    setIsLoading(false);
  }, [businessId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleEnabled = async (offer: Offer, next: boolean) => {
    // Optimistic.
    setOffers((prev) => prev.map((o) => (o.id === offer.id ? { ...o, enabled: next } : o)));
    const { error } = await supabase
      .from("business_offers")
      .update({ enabled: next, updated_at: new Date().toISOString() })
      .eq("id", offer.id);
    if (error) {
      setOffers((prev) => prev.map((o) => (o.id === offer.id ? { ...o, enabled: !next } : o)));
      toast({ title: "Couldn't update offer", description: error.message, variant: "destructive" });
    }
  };

  const deleteOffer = async (offer: Offer) => {
    const { error } = await supabase.from("business_offers").delete().eq("id", offer.id);
    if (error) {
      toast({ title: "Couldn't delete offer", description: error.message, variant: "destructive" });
      return;
    }
    setOffers((prev) => prev.filter((o) => o.id !== offer.id));
    toast({ title: "Offer deleted" });
  };

  const saveDraft = async () => {
    if (!businessId || !draft) return;

    if (!draft.label.trim()) {
      toast({ title: "Add a name for this offer", variant: "destructive" });
      return;
    }
    if (draft.offer_type === "perk") {
      if (!draft.perk_text.trim()) {
        toast({ title: "Describe the perk", variant: "destructive" });
        return;
      }
    } else if (!draft.value.trim() || isNaN(Number(draft.value))) {
      toast({ title: "Enter a valid amount", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    const row = {
      business_id: businessId,
      enabled: draft.enabled,
      label: draft.label.trim(),
      offer_type: draft.offer_type,
      value: draft.offer_type === "perk" ? null : Number(draft.value),
      perk_text: draft.offer_type === "perk" ? draft.perk_text.trim() : null,
      max_value: draft.max_value.trim() ? Number(draft.max_value) : null,
      min_spend: draft.min_spend.trim() ? Number(draft.min_spend) : null,
      expiry_days: draft.expiry_days.trim() ? Math.round(Number(draft.expiry_days)) : null,
      tier: draft.tier,
      applies_to: draft.applies_to.trim() || null,
    };

    const { error } = await supabase.from("business_offers").insert(row);
    setIsSaving(false);
    if (error) {
      toast({ title: "Couldn't save offer", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Offer added" });
    setDraft(null);
    load();
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <p className="text-sm font-bold uppercase tracking-wider text-foreground">Win-back offers</p>
        <p className="text-sm text-muted-foreground leading-snug">
          Incentives your AI may offer when winning back dormant customers. The agent only ever uses offers you create here — it never invents a discount. Target everyone, or just high-value or low-engagement customers.
        </p>
      </div>

      {/* Existing offers */}
      {offers.length === 0 ? (
        <p className="text-sm text-muted-foreground">No offers yet. Add one below to let the AI sweeten its win-back messages.</p>
      ) : (
        <div className="space-y-3">
          {offers.map((o) => (
            <div key={o.id} className="flex items-center justify-between gap-3 border border-border rounded-lg p-4">
              <div className="flex items-start gap-3 min-w-0">
                <Tag className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{o.label}</p>
                  <p className="text-sm text-muted-foreground truncate">{summarize(o)}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{TIER_LABELS[o.tier]}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <Switch
                  checked={o.enabled}
                  onCheckedChange={(v) => toggleEnabled(o, v)}
                  className="data-[state=checked]:bg-primary"
                  aria-label="Toggle offer"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => deleteOffer(o)}
                  aria-label="Delete offer"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add form */}
      {draft ? (
        <div className="border border-border rounded-lg p-4 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm">Offer name (for your reference)</Label>
            <Input
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              placeholder="e.g. Welcome back 15%"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm">Type</Label>
              <Select value={draft.offer_type} onValueChange={(v) => setDraft({ ...draft, offer_type: v as OfferType })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percentage off</SelectItem>
                  <SelectItem value="fixed">Fixed amount off</SelectItem>
                  <SelectItem value="perk">Perk / freebie</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {draft.offer_type === "perk" ? (
              <div className="space-y-1.5">
                <Label className="text-sm">Perk description</Label>
                <Input
                  value={draft.perk_text}
                  onChange={(e) => setDraft({ ...draft, perk_text: e.target.value })}
                  placeholder="e.g. a complimentary add-on"
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-sm">{draft.offer_type === "percent" ? "Percent off" : "Amount off ($)"}</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={draft.value}
                  onChange={(e) => setDraft({ ...draft, value: e.target.value })}
                  placeholder={draft.offer_type === "percent" ? "15" : "10"}
                />
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm">Applies to <span className="text-muted-foreground">— optional</span></Label>
            <Input
              value={draft.applies_to}
              onChange={(e) => setDraft({ ...draft, applies_to: e.target.value })}
              placeholder="e.g. all services, or consultations only, or first-time bookings only"
            />
            <p className="text-xs text-muted-foreground">
              Free-text scope. The AI uses this exactly — it will never broaden what you write. Leave empty for a generic "your next visit".
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm">Min spend ($) <span className="text-muted-foreground">— optional</span></Label>
              <Input
                type="number"
                inputMode="decimal"
                value={draft.min_spend}
                onChange={(e) => setDraft({ ...draft, min_spend: e.target.value })}
                placeholder="—"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Valid for (days) <span className="text-muted-foreground">— optional</span></Label>
              <Input
                type="number"
                inputMode="numeric"
                value={draft.expiry_days}
                onChange={(e) => setDraft({ ...draft, expiry_days: e.target.value })}
                placeholder="—"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Who it's for</Label>
              <Select value={draft.tier} onValueChange={(v) => setDraft({ ...draft, tier: v as OfferTier })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All dormant customers</SelectItem>
                  <SelectItem value="high_value">High-value customers</SelectItem>
                  <SelectItem value="low_engagement">Low-engagement customers</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => setDraft(null)} disabled={isSaving}>Cancel</Button>
            <Button onClick={saveDraft} disabled={isSaving} className="min-w-[110px]">
              {isSaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : "Add offer"}
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" className="gap-2" onClick={() => setDraft(emptyDraft())}>
          <Plus className="h-4 w-4" />
          Add an offer
        </Button>
      )}
    </div>
  );
};

export default WinbackOffers;
